/**
 * Håndterer to ting:
 * 1) "Ny version tilgængelig" — service worker-opdatering (registerType: prompt)
 * 2) Installations-hint på mobil (Android beforeinstallprompt + iOS-instruktion)
 */
import { useEffect, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { Button } from "@/components/ui/button";
import { X, Share, Download, RefreshCw } from "lucide-react";

const INSTALL_DISMISSED_KEY = "lab3d_install_dismissed";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/** Kører vi som native app (Capacitor)? Så er install-hint + SW-opdatering irrelevante. */
function isNativeApp(): boolean {
  const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return typeof cap?.isNativePlatform === "function" && cap.isNativePlatform();
}

export function PWAPrompts() {
  if (isNativeApp()) return null;
  return <PWAPromptsInner />;
}

function PWAPromptsInner() {
  const { needRefresh, updateServiceWorker } = useRegisterSW();
  const [needsRefresh, setNeedsRefresh] = needRefresh;
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(INSTALL_DISMISSED_KEY)) return;
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as { standalone?: boolean }).standalone === true;
    if (isStandalone) return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    if (isIos) {
      const timer = setTimeout(() => setShowIosHint(true), 5_000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismissInstall = () => {
    localStorage.setItem(INSTALL_DISMISSED_KEY, new Date().toISOString());
    setInstallEvent(null);
    setShowIosHint(false);
  };

  if (needsRefresh) {
    return (
      <div className="fixed inset-x-3 bottom-20 z-[100] mx-auto flex max-w-md items-center gap-3 rounded-lg border bg-card p-3 shadow-lg md:bottom-4">
        <RefreshCw className="h-5 w-5 shrink-0 text-primary" />
        <p className="flex-1 text-sm">Ny version af portalen er klar.</p>
        <Button size="sm" onClick={() => void updateServiceWorker(true)}>Opdatér</Button>
        <button aria-label="Luk" onClick={() => setNeedsRefresh(false)} className="p-2 text-muted-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  if (installEvent) {
    return (
      <div className="fixed inset-x-3 bottom-20 z-[100] mx-auto flex max-w-md items-center gap-3 rounded-lg border bg-card p-3 shadow-lg md:bottom-4">
        <Download className="h-5 w-5 shrink-0 text-primary" />
        <p className="flex-1 text-sm">Installér LAB3D B2B som app på din enhed.</p>
        <Button
          size="sm"
          onClick={() => {
            void installEvent.prompt();
            void installEvent.userChoice.finally(dismissInstall);
          }}
        >
          Installér
        </Button>
        <button aria-label="Luk" onClick={dismissInstall} className="p-2 text-muted-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  if (showIosHint) {
    return (
      <div className="fixed inset-x-3 bottom-20 z-[100] mx-auto flex max-w-md items-center gap-3 rounded-lg border bg-card p-3 shadow-lg">
        <Share className="h-5 w-5 shrink-0 text-primary" />
        <p className="flex-1 text-sm">
          Installér appen: tryk på <span className="font-medium">Del</span> og vælg{" "}
          <span className="font-medium">Føj til hjemmeskærm</span>.
        </p>
        <button aria-label="Luk" onClick={dismissInstall} className="p-2 text-muted-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }
  return null;
}
